import {
  Building2,
  Button,
  Loader2,
  Search,
} from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDebounce } from "../../../hooks/useDebounce";
import { facilitiesApi } from "../../../services/facilities.api";
import managerApi from "../../../services/manager.api";
import { FacilityResponse } from "../../../types/facility.definition";
import { Manager } from "../../../types/manager.definition";
import { Modal } from "../Modal/Modal";

interface AssignFacilityModalProps {
  isOpen: boolean;
  onClose: () => void;
  manager: Manager;
  onSuccess: () => void;
}

/**
 * MED-1470: picks the facility a manager is put in charge of and assigns it through
 * `PUT /managers/{id}/facility/{facilityId}`. The manager list used to open a modal
 * whose "Assigner" button closed it without calling anything.
 */
export const AssignFacilityModal: React.FC<AssignFacilityModalProps> = ({
  isOpen,
  onClose,
  manager,
  onSuccess,
}) => {
  const { t } = useTranslation();

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<FacilityResponse[] | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const debouncedQuery = useDebounce(query, 500);

  useEffect(() => {
    if (!isOpen) return;
    setQuery("");
    setResults(null);
    setSearchError(null);
    setSubmitError(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    if (debouncedQuery.trim().length < 2) {
      setResults(null);
      setSearchError(null);
      return;
    }
    setSearchLoading(true);
    setSearchError(null);
    facilitiesApi
      .searchFacilities(debouncedQuery.trim())
      .then(found => setResults(found))
      .catch(() => {
        setSearchError(t("admin.managers.assignFacilityModal.loadError"));
        setResults(null);
      })
      .finally(() => setSearchLoading(false));
  }, [debouncedQuery, isOpen, t]);

  const handleAssign = async (facility: FacilityResponse) => {
    setSubmitting(true);
    setSubmitError(null);
    try {
      await managerApi.assignFacility(manager.id, facility.id);
      onSuccess();
      onClose();
    } catch {
      setSubmitError(t("admin.managers.assignFacilityModal.error"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => !submitting && onClose()}
      title={t("admin.managers.assignFacilityModal.title")}
    >
      <div className="p-6 space-y-4">
        <p className="text-sm text-neutral-600">
          {t("admin.managers.assignFacilityModal.subtitle", {
            name: `${manager.firstName} ${manager.lastName}`,
          })}
        </p>

        <div className="relative">
          <div className="flex absolute inset-y-0 left-3 items-center pointer-events-none">
            <Search className="w-4 h-4 text-gray-400" />
          </div>
          <input
            type="text"
            value={query}
            onChange={e => setQuery(e.target.value)}
            placeholder={t("admin.managers.assignFacilityModal.search")}
            aria-label={t("admin.managers.assignFacilityModal.search")}
            className="py-2.5 pr-4 pl-10 w-full text-sm rounded-lg border border-gray-300 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
          />
        </div>

        {searchLoading && (
          <div className="flex gap-2 items-center text-sm text-neutral-500">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>{t("admin.managers.assignFacilityModal.searching")}</span>
          </div>
        )}

        {searchError && !searchLoading && (
          <p className="text-sm text-danger-600">{searchError}</p>
        )}

        {!searchLoading && !searchError && results !== null && (
          <>
            {results.length === 0 ? (
              <p className="text-sm text-neutral-600">
                {t("admin.managers.assignFacilityModal.noFacilities")}
              </p>
            ) : (
              <ul className="overflow-y-auto max-h-64 rounded-lg border divide-y divide-neutral-100 border-neutral-200">
                {results.map(facility => (
                  <li key={facility.id}>
                    <button
                      type="button"
                      disabled={submitting}
                      onClick={() => handleAssign(facility)}
                      className="flex gap-3 items-start p-4 w-full text-left transition-colors hover:bg-primary-50 disabled:opacity-50"
                    >
                      <Building2 className="flex-shrink-0 mt-0.5 w-5 h-5 text-primary-500" />
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium truncate text-neutral-900">
                          {facility.name}
                        </span>
                        <span className="block mt-0.5 text-sm truncate text-neutral-500">
                          {[facility.location?.address, facility.location?.city]
                            .filter(Boolean)
                            .join(", ") || "—"}
                        </span>
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}

        {!searchLoading && !searchError && results === null && (
          <p className="text-sm text-neutral-500">
            {t("admin.managers.assignFacilityModal.searchHint")}
          </p>
        )}

        {submitError && (
          <p className="text-sm text-danger-600">{submitError}</p>
        )}
      </div>

      <div className="flex justify-end px-6 py-4 border-t border-neutral-200">
        <Button variant="outline" onClick={onClose} disabled={submitting}>
          {t("common.cancel")}
        </Button>
      </div>
    </Modal>
  );
};
